import { useScrollReveal } from '@/hooks/useScrollReveal';
import { LiveSpeakIcon, LiveInsightIcon } from '@/components/icons/ProductIcons';
import { GraduationCap } from 'lucide-react';

const products = [
  {
    name: 'Speak',
    tagline: 'Deterministic reporting.',
    paragraphs: [
      'Dictate naturally. Your words stay yours.',
      'Reports are compiled, not guessed, with explicit structure and traceable logic.',
      'Different voices, one consistent framework for quality review.',
    ],
    icon: 'speak',
  },
  {
    name: 'Insight',
    tagline: 'Your clinical copilot.',
    paragraphs: [
      'Differentials, pitfalls, and teaching points delivered at the point of interpretation.',
      'Less time searching external sites, more time reading studies with confidence.',
    ],
    icon: 'insight',
  },
];

function ProductBlock({ product, index }: { product: (typeof products)[number]; index: number }) {
  const { ref, isVisible } = useScrollReveal();
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`h-screen flex items-center scroll-snap-align-start ${isVisible ? 'reveal-visible' : 'reveal-hidden'}`}
    >
      <div
        className={`max-w-5xl mx-auto px-4 w-full flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12 md:gap-20`}
      >
        {/* Product Logo */}
        <div className="flex-shrink-0">
          {product.icon === 'speak' && (
            <img src="/logo-speak.jpg" alt="LiveSpeak" className="w-24 h-24 rounded-2xl object-cover" />
          )}
          {product.icon === 'insight' && (
            <img src="/logo-insight.jpg" alt="LiveInsight" className="w-24 h-24 rounded-2xl object-cover" />
          )}
          {product.icon === 'rounds' && (
            <div className="w-24 h-24 flex items-center justify-center rounded-2xl bg-primary/5 border border-primary/10">
              <GraduationCap className="h-12 w-12 text-primary" />
            </div>
          )}
        </div>

        {/* Text */}
        <div className={`flex-1 ${isEven ? 'md:text-left' : 'md:text-right'} text-center`}>
          <p className="text-sm font-medium tracking-wide mb-3">
            <span className="text-muted-foreground">RadiologyLive.ai</span>{' '}
            <span className="text-primary">{product.name}</span>
          </p>
          <h3 className="text-2xl sm:text-3xl font-light text-foreground mb-4">
            {product.tagline}
          </h3>
          <div className="space-y-3 max-w-lg mx-auto md:mx-0">
            {product.paragraphs.map((p, j) => (
              <p key={j} className="text-lg text-muted-foreground font-light leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductShowcase() {
  return (
    <section>
      {products.map((product, i) => (
        <ProductBlock key={product.name} product={product} index={i} />
      ))}
    </section>
  );
}
